import axios, { AxiosError } from 'axios';
import { isUnAuthorized } from 'helpers/api/is-unauthorized';
import { useAuthStore } from 'store/AuthStore';
import { useUserStore } from 'store/UserStore';

export const API = { URL: process.env.REACT_APP_API_URL };
export const MAX_REFRESH_TOKEN_RETRY_COUNT = 1;

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    retries: number;
  }
}

export const api = axios.create({
  baseURL: API.URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().tokens.access_token;

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const { actions: authActions, isLoggedIn } = useAuthStore.getState();
    const { actions: userActions } = useUserStore.getState();

    if (!isUnAuthorized(error) || !isLoggedIn) return Promise.reject(error);

    try {
      if (error.config) {
        const originalRequest = error.config;

        originalRequest.retries ||= 0;

        if (originalRequest.retries < MAX_REFRESH_TOKEN_RETRY_COUNT) {
          originalRequest.retries += 1;

          await authActions.refreshSession();

          return api(originalRequest);
        } else {
          if (originalRequest.retries === MAX_REFRESH_TOKEN_RETRY_COUNT) {
            authActions.logout();
            userActions.clearUserDetail();
          }
        }
      }
    } catch (err) {
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export const bareApi = axios.create({
  baseURL: API.URL,
  headers: {
    'Content-Type': 'application/json',
  },
});
